import { useState, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, GripVertical, Trash2 } from "lucide-react";
import "./index.css";

interface Todo {
	id: string;
	text: string;
	completed: boolean;
}

function generateId() {
	return Math.random().toString(36).slice(2, 10);
}

export function App() {
	const [todos, setTodos] = useState<Todo[]>([
		{ id: generateId(), text: "Try dragging this task", completed: false },
		{ id: generateId(), text: "Click checkbox to complete", completed: false },
		{ id: generateId(), text: "Add new todos below", completed: true },
	]);
	const [newTodo, setNewTodo] = useState("");
	const [draggedId, setDraggedId] = useState<string | null>(null);
	const [dragOverId, setDragOverId] = useState<string | null>(null);
	const dragCounter = useRef<Map<string, number>>(new Map());

	const addTodo = () => {
		const text = newTodo.trim();
		if (!text) return;
		setTodos((prev) => [...prev, { id: generateId(), text, completed: false }]);
		setNewTodo("");
	};

	const toggleTodo = (id: string) => {
		setTodos((prev) =>
			prev.map((t) => (t.id === id ? { ...t, completed: !t.completed } : t))
		);
	};

	const deleteTodo = (id: string) => {
		setTodos((prev) => prev.filter((t) => t.id !== id));
	};

	const handleDragStart = useCallback(
		(e: React.DragEvent, id: string) => {
			setDraggedId(id);
			e.dataTransfer.effectAllowed = "move";
			e.dataTransfer.setData("text/plain", id);
		},
		[]
	);

	const handleDragEnter = useCallback(
		(e: React.DragEvent, id: string) => {
			e.preventDefault();
			const count = (dragCounter.current.get(id) || 0) + 1;
			dragCounter.current.set(id, count);
			if (count === 1) {
				setDragOverId(id);
			}
		},
		[]
	);

	const handleDragLeave = useCallback(
		(e: React.DragEvent, id: string) => {
			e.preventDefault();
			const count = (dragCounter.current.get(id) || 0) - 1;
			dragCounter.current.set(id, count);
			if (count <= 0) {
				dragCounter.current.delete(id);
				setDragOverId((prev) => (prev === id ? null : prev));
			}
		},
		[]
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		e.dataTransfer.dropEffect = "move";
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent, targetId: string) => {
			e.preventDefault();
			dragCounter.current.clear();
			setDragOverId(null);

			if (!draggedId || draggedId === targetId) {
				setDraggedId(null);
				return;
			}

			setTodos((prev) => {
				const items = [...prev];
				const fromIndex = items.findIndex((t) => t.id === draggedId);
				const toIndex = items.findIndex((t) => t.id === targetId);
				if (fromIndex === -1 || toIndex === -1) return prev;
				const [moved] = items.splice(fromIndex, 1);
				items.splice(toIndex, 0, moved);
				return items;
			});

			setDraggedId(null);
		},
		[draggedId]
	);

	const handleDragEnd = useCallback(() => {
		dragCounter.current.clear();
		setDraggedId(null);
		setDragOverId(null);
	}, []);

	const activeTodos = todos.filter((t) => !t.completed);
	const completedTodos = todos.filter((t) => t.completed);
	const completedCount = completedTodos.length;
	const totalCount = todos.length;

	return (
		<div className="w-full max-w-lg mx-auto p-4 sm:p-6">
			<Card className="shadow-lg">
				<CardHeader className="pb-4">
					<CardTitle className="text-2xl font-bold tracking-tight">
						Todos
					</CardTitle>
					<p className="text-sm text-muted-foreground">
						{completedCount} of {totalCount} completed
					</p>
				</CardHeader>
				<CardContent className="flex flex-col gap-4">
					{/* Add todo form */}
					<form
						onSubmit={(e) => {
							e.preventDefault();
							addTodo();
						}}
						className="flex gap-2"
					>
						<Input
							value={newTodo}
							onChange={(e) => setNewTodo(e.target.value)}
							placeholder="Add a new task..."
							className="flex-1"
						/>
						<Button type="submit" size="icon" disabled={!newTodo.trim()}>
							<Plus />
						</Button>
					</form>

					{/* Todo list */}
					{todos.length === 0 && (
						<p className="text-center text-muted-foreground py-8 text-sm">
							No todos yet. Add one above!
						</p>
					)}

					<div className="flex flex-col gap-1">
						{activeTodos.map((todo) => (
							<TodoItem
								key={todo.id}
								todo={todo}
								isDragging={draggedId === todo.id}
								isDragOver={dragOverId === todo.id}
								onToggle={toggleTodo}
								onDelete={deleteTodo}
								onDragStart={handleDragStart}
								onDragEnter={handleDragEnter}
								onDragLeave={handleDragLeave}
								onDragOver={handleDragOver}
								onDrop={handleDrop}
								onDragEnd={handleDragEnd}
							/>
						))}
					</div>

					{completedTodos.length > 0 && (
						<>
							<div className="flex items-center gap-2 pt-2">
								<div className="h-px flex-1 bg-border" />
								<span className="text-xs text-muted-foreground font-medium">
									Completed ({completedTodos.length})
								</span>
								<div className="h-px flex-1 bg-border" />
							</div>
							<div className="flex flex-col gap-1">
								{completedTodos.map((todo) => (
									<TodoItem
										key={todo.id}
										todo={todo}
										isDragging={draggedId === todo.id}
										isDragOver={dragOverId === todo.id}
										onToggle={toggleTodo}
										onDelete={deleteTodo}
										onDragStart={handleDragStart}
										onDragEnter={handleDragEnter}
										onDragLeave={handleDragLeave}
										onDragOver={handleDragOver}
										onDrop={handleDrop}
										onDragEnd={handleDragEnd}
									/>
								))}
							</div>
						</>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

interface TodoItemProps {
	todo: Todo;
	isDragging: boolean;
	isDragOver: boolean;
	onToggle: (id: string) => void;
	onDelete: (id: string) => void;
	onDragStart: (e: React.DragEvent, id: string) => void;
	onDragEnter: (e: React.DragEvent, id: string) => void;
	onDragLeave: (e: React.DragEvent, id: string) => void;
	onDragOver: (e: React.DragEvent) => void;
	onDrop: (e: React.DragEvent, id: string) => void;
	onDragEnd: () => void;
}

function TodoItem({
	todo,
	isDragging,
	isDragOver,
	onToggle,
	onDelete,
	onDragStart,
	onDragEnter,
	onDragLeave,
	onDragOver,
	onDrop,
	onDragEnd,
}: TodoItemProps) {
	return (
		<div
			draggable
			onDragStart={(e) => onDragStart(e, todo.id)}
			onDragEnter={(e) => onDragEnter(e, todo.id)}
			onDragLeave={(e) => onDragLeave(e, todo.id)}
			onDragOver={onDragOver}
			onDrop={(e) => onDrop(e, todo.id)}
			onDragEnd={onDragEnd}
			className={`
				group flex items-center gap-2 px-3 py-2.5 rounded-lg border
				transition-all duration-150 cursor-grab active:cursor-grabbing
				${isDragging ? "opacity-40 scale-95" : "opacity-100"}
				${isDragOver ? "border-primary bg-primary/5 shadow-sm" : "border-transparent hover:bg-muted/50"}
			`}
		>
			<GripVertical className="size-4 text-muted-foreground/40 group-hover:text-muted-foreground shrink-0 transition-colors" />
			<Checkbox
				checked={todo.completed}
				onCheckedChange={() => onToggle(todo.id)}
				className="shrink-0"
			/>
			<span
				className={`flex-1 text-sm transition-all ${
					todo.completed
						? "line-through text-muted-foreground"
						: "text-foreground"
				}`}
			>
				{todo.text}
			</span>
			<Button
				variant="ghost"
				size="icon"
				onClick={() => onDelete(todo.id)}
				className="size-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
			>
				<Trash2 className="size-3.5" />
			</Button>
		</div>
	);
}

export default App;
